import { FC, MouseEventHandler, RefObject, useRef } from 'react';
import { ICON_NAMES, ITestable } from '@belong/types';
import { Icon } from '../../Icon/Icon';
import { A11yCopy } from '../../styles/Typography/A11yCopy';
import { ITextInput } from '../TextInput';
import * as Search from './SearchBox.styles';

export interface ISearchBox extends ITextInput, ITestable {
  a11yClear: string;
  handleClear: MouseEventHandler<HTMLButtonElement>;
  hasSelection: boolean;
  id?: string;
  isBusy?: boolean;
  a11yBusy?: string;
  searchIcon?: boolean;
  expandIcon?: boolean;
  refProp?: RefObject<HTMLInputElement | null>;
}

/**
 * If you are looking for typeahead functionality, have a look to Combobox.
 */
export const SearchBox: FC<ISearchBox> = ({
  a11yClear,
  handleClear,
  a11yBusy,
  isBusy,
  searchIcon = false,
  expandIcon = false,
  hasSelection,
  refProp,
  ...inputProps
}: ISearchBox) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { id, value } = inputProps;
  const idBusy = isBusy ? `${id}Busy` : undefined;
  const describedBy = inputProps['aria-describedby'];
  const shouldShowClearBtn = !!value?.toString().trim() || hasSelection;
  const newInputProps = {
    ...inputProps,
    'aria-describedby': isBusy ? `${describedBy || ''} ${idBusy}` : describedBy
  };

  // typescript refuses to take 2 interfaces for searchbox
  // so I console.error if the props are incorrect
  if (isBusy && (!id || !a11yBusy)) {
    // eslint-disable-next-line no-console
    console.error('Searchbox is missing "id" or "a11yBusy" when "isBusy" === true');
  }

  return (
    <Search.Wrapper>
      {isBusy && <Search.Progress />}
      {searchIcon && (
        <Search.IconWrapper
          aria-hidden
          onClick={() => {
            const ref = refProp || inputRef;
            if (ref && ref.current) {
              ref.current.focus();
            }
          }}
        >
          <Icon name={ICON_NAMES.Search} />
        </Search.IconWrapper>
      )}
      <Search.Input
        ref={(refProp || inputRef) as RefObject<HTMLInputElement>}
        id={id}
        searchIcon={searchIcon}
        aria-invalid={newInputProps.isError}
        {...newInputProps}
      />
      {shouldShowClearBtn && (
        <Search.ClearBtn onClick={handleClear} aria-label={a11yClear} onDarkBG={newInputProps.onDarkBG}>
          Clear
        </Search.ClearBtn>
      )}
      {!shouldShowClearBtn && expandIcon && <Search.ExpandIcon />}
      {isBusy && (
        <A11yCopy id={idBusy} aria-live="polite">
          {a11yBusy}
        </A11yCopy>
      )}
    </Search.Wrapper>
  );
};

SearchBox.displayName = 'SearchBox';
